import React from "react";
import PropTypes from "prop-types";
import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import typeCheckingStore from "data/util/typeCheckingStore";
import OverActions from "data/OverActions";
import RecordActions, { OPT_USER_CONFIRMED } from "data/RecordActions";
import TableStore, { isUnconfirmedVersionChange } from "data/TableStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import SuperRecordActions from "data/SuperRecordActions";
import StereotypesStore from "data/StereotypesStore";
import SuperRecordStore from "data/SuperRecordStore";
import ActiveFormActions from "data/ActiveFormActions";
import ActiveFormStore from "data/ActiveFormStore";
import { lastBlurredValidation } from "data/selectors/records";
import EntityName from "data/util/EntityName";
import userStore from "data/userStore";
import {
    isIllegal,
    isOk,
    isWarning,
} from "jsmodel/utils";
import NavigationActions from "data/NavigationActions";
import FormStore from "data/meta/FormStore";
import FormActions from "data/meta/FormActions";
import NavigatorStore from "data/NavigatorStore";
import AdtActions from "data/AdtActions";
import HemoActions from "data/HemoActions";
import MappingActions from "data/MappingActions";
import { canSave } from "data/util/saveit";
import MapButtonActions from "data/MapButtonActions";
import MapDialogActions from "data/MapDialogActions";
import MappingStore from "data/MappingStore";
import MergeStore from "data/MergeStore";
import { getAutoMergeGlobalSummary } from "data/util/merge/mergeHelpers";
import MergeActions from "data/MergeActions";
import userEditedRecordStore from "data/userEditedRecordStore";
import GxepActions from "data/GxepActions";
import UserActions from "data/UserActions";
import SearchResultActions from "data/SearchResultActions";
import SearchFormActions from "data/SearchFormActions";
import LockActions from "data/LockActions";
import lockStore, { LockTypes } from "data/lockStore";
import hasUnsavedChanges from "data/selectors/hasUnsavedChanges";
import oxfordList from "lib/oxfordList";
import ExportParamActions from "./ExportParamActions";
import ModalPrompt from "../lib/over/ModalPrompt";
import BaseServices from "../lib/BaseServices";
import SessionActions from "./SessionActions";

const ENTITY_FORM_OVER_KEY = "agg:entity-form";
const MAPPING_PLUGIN_SEARCH_KEY = "mapping:plugin-search";
const MAPPING_WORKFLOW_KEY = "mapping:workflow";
const MERGE_WORKFLOW_KEY = "merge:workflow";
const SUPER_RECORD_DELETE_KEY = "super-record-delete";
const EXPORT_PARAM_SET_KEY = "export-param-set";
const SESSION_EXPIRING_KEY = "session-expiration-imminent";
const CONNECTION_INTERRUPTED_KEY = "connection-interrupted";
const AUTO_KEY_PREFIX = "-auto-key-";

/**
 * I accept an 'options' object with the following keys to show as an over:
 *
 * -    **message**: the message to display in the box. Can be a simple string,
 *      a string with HTML in it (deprecated), or a zero-arg function returning
 *      a React render tree. (default `"Loading..."`)
 * -    **actions**: an array of objects, each with 'label' and 'callback'
 *      keys to render as a row of buttons at the bottom of the box (default
 *      `[]`)
 * -    **spinner**: whether to show the spinner above the message (default
 *      `true` if actions is empty, `false` otherwise)
 * -    **title**: a title for the dialog (default `null`)
 * -    **autoHide**: whether to hide the modal when an action button is
 *      clicked and the callback returns (default `true`)
 * -    **key**: a unique string which can be passed to 'over:hide' to close
 *      the the same over (default `null`)
 *
 * For backwards compatibility reasons, I can accept a positional parameter
 * corresponding to the 'message' option value.  No positional equivalent is
 * available for other options.
 */
export const overOptions = (options, createMissingKey) => {
    if (options == null) { options = {}; }
    if (createMissingKey == null) { createMissingKey = true; }
    if (typeof options === "string" || typeof options === "function") {
        options = {
            message: options,
        };
    }
    options = {
        __at: Date.now(),
        message: "Loading...",
        actions: [],
        title: null,
        autoHide: true,
        key: null,
        ...options,
    };
    if (!options.hasOwnProperty("interactive")) {
        options.interactive = options.actions.length > 0;
    }
    if (!options.hasOwnProperty("spinner")) {
        options.spinner = !options.interactive;
    }
    if (createMissingKey && options.key == null) {
        options.key = AUTO_KEY_PREFIX + Math.random();
    }
    return options;
};

export const isModalShowing = state =>
    !isEmpty(state);

export const isEmpty = state =>
    state.length === 0;

const _is_interactive = it => {
    if (!it) return false;
    if (it.hasOwnProperty("interactive")) return it.interactive;
    return it.actions && it.actions.length > 0;

};

export const getActiveItem = state => {
    const ist = state.filter(_is_interactive);
    if (ist.length > 0) {
        return ist[ist.length - 1];
    }
    return state[state.length - 1];
};

export const isModalInteractive = state =>
    _is_interactive(getActiveItem(state));

// reducer helpers
const show = (state, options) => {
    const newState = state.slice();
    options = overOptions(options);
    newState.push(options);
    return newState;
};

const isShowing = (state, key) =>
    state.some(it => it.key === key);

const showUnique = (state, key, options) =>
    isShowing(state, key)
        ? state
        : show(state, {
            ...options,
            key,
        });

const hide = (state, key, allInstancesOfKey = false) => {
    const newState = state.slice();
    for (let i = newState.length - 1; i >= 0; i--) {
        const it = newState[i];
        const isKey = it.key === key;
        if (isKey || (key == null && it.key.indexOf(AUTO_KEY_PREFIX) === 0)) {
            newState.splice(i, 1);
            if (isKey && allInstancesOfKey) continue;
            break;
        }
    }
    return newState;
};

export const isConfirmed = action =>
    action.hasOwnProperty(OPT_USER_CONFIRMED) && action[OPT_USER_CONFIRMED];

const confirmDispatch = (
    state,
    action,
    message,
    yesLabel = "Yes",
    noLabel = "No",
) =>
    show(state, {
        message,
        spinner: false,
        actions: [
            {
                label: yesLabel,
                callback: () => {
                    const a = {
                        ...action,
                        [OPT_USER_CONFIRMED]: true,
                    };
                    if (a.hasOwnProperty(ACTION_CONTEXT_KEY)) {
                        const ctx = a[ACTION_CONTEXT_KEY];
                        delete a[ACTION_CONTEXT_KEY];
                        Dispatcher.contextDispatch(ctx, a);
                    } else {
                        Dispatcher.dispatch(a);
                    }
                },
            },
            {
                label: noLabel,
                callback: () => {
                    /* noop */
                },
            },
        ],
    });

const confirmDelete = (state, action, ep) => confirmDispatch(
    state,
    action,
    () => <React.Fragment>
        <p>
            Are you sure you would like to delete
            this {ep.entityInfo.name} {ep.getLabel()} record?
        </p>
        <p>
            (Note that you will need to save your changes with the SAVE button
            for the deletion to be saved.)
        </p>
    </React.Fragment>,
);

const callbackAndHideBuilder = (callback, key) =>
    (...args) => {
        callback(...args);
        Dispatcher.dispatch({
            type: OverActions.HIDE,
            key,
        });
    };

// I mimic window.prompt, but via callback, not blocking. The callback will be
// passed the entered value on ok or null on cancel.
const showPrompt = (state, title, label, initialValue, callback) => {
    const key = `prompt-${Date.now()}`;
    return show(state, {
        key,
        title,
        interactive: true,
        messageIsUi: true,
        message: () => <ModalPrompt
            title={title}
            label={label}
            initialValue={initialValue}
            callback={callbackAndHideBuilder(callback, key)}
        />,
    });
};

const showAlert = (state, message, titleOrCallback, callback, actions) => {
    if (typeof titleOrCallback === "function") {
        callback = titleOrCallback;
        titleOrCallback = undefined;
    }
    return show(state, {
        message,
        title: titleOrCallback, // likely undefined
        spinner: false,
        actions: actions && actions.length > 0
            ? actions
            : [ okAction(callback) ],
    });
};

const okAction = callback => ({
    label: "Ok",
    keyCode: 27, // trigger on escape
    callback,
});

const showOptLockAlert = (state, violators, ctxKey, alertKey, key, id, type=LockTypes.LAYER) => {
    if (!alertKey) alertKey = ctxKey;
    alertKey = "optlock-" + alertKey;
    const recordLabel = TableStore.getStereotypeProxy(key, id).getLabel();
    const attribution = oxfordList(violators);
    let message, callback;
    if (type === LockTypes.MERGE_SOURCE) {
        message = <React.Fragment>
            <p>Sorry, <strong>{recordLabel}</strong> has been updated by {attribution}.</p>
            <p>Please click OK to see their updates. </p>
        </React.Fragment>;
        callback = () => Dispatcher.contextDispatch(ctxKey, {
            type: MergeActions.CANCEL_FOR_SOURCE_OPTLOCK,
            key,
            id,
        });
    } else { // layer
        message = <React.Fragment>
            <p>Sorry, <strong>{recordLabel}</strong> has been updated by {attribution}.</p>
            <p>Please click OK to see their updates. Any updates you've made
                since you opened or last saved the record must be
                re-entered.</p>
        </React.Fragment>;
        callback = () => Dispatcher.contextDispatch(ctxKey, {
            type: SuperRecordActions.CANCEL_FOR_OPTLOCK,
            key,
            id,
        });
    }
    return show(hide(state, alertKey), {
        key: alertKey,
        message: () => message,
        spinner: false,
        actions: [{
            label: "Ok",
            keyCode: 27, // trigger on escape
            callback,
        }],
    });
};

class OverStore extends ReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialState() {
        return [];
    }

    reduce(state, action) {
        switch (action.type) {

            // old-style explicit actions
            case OverActions.SHOW:
                return show(state, action.options);
            case OverActions.HIDE:
                return hide(state, action.key);
            case OverActions.APP_ALERT:
                return showAlert(
                    state,
                    action.message,
                    action.title,
                    action.callback,
                    action.actions,
                );

            case SessionActions.timeoutImminent:
                /*
                 * Do "showUnique" manually. We don't want to skip if there is
                 * one already, we want to replace it.
                 */
                state = hide(state, SESSION_EXPIRING_KEY, true);
                const secLeft = action.timeLeft / 1000;
                let msg;
                if (secLeft > 90) {
                    msg = `${Math.round(secLeft / 60)} minutes`;
                } else if (secLeft > 45) {
                    msg = "one minute";
                } else {
                    msg = "a few seconds";
                }
                return show(
                    state,
                    {
                        key: SESSION_EXPIRING_KEY,
                        message: `Your session will expire in ${msg}`,
                        actions: [ {
                            label: "I'm Still Here!",
                            callback: () =>
                                Backbone.dispatcher.trigger("user:activity"),
                        }, {
                            label: "Logout Now",
                            callback: () =>
                                Backbone.dispatcher.trigger("user:logout"),
                        } ],
                    },
                );

            case OverActions.BROWSER_OFFLINE:
            case OverActions.ARMUS_UNREACHABLE:
                return showUnique(
                    state,
                    CONNECTION_INTERRUPTED_KEY,
                    {
                        message: "There has been an interruption in your connection; please try again.",
                        actions: [ okAction() ],
                    },
                );

            case OverActions.API_UNREACHABLE:
                return showUnique(
                    state,
                    CONNECTION_INTERRUPTED_KEY,
                    {
                        message: "There has been an interruption in your connection; please wait a few moments and try again.",
                        actions: [ okAction() ],
                    },
                );

            case OverActions.HTTP_UNAUTHORIZED:
                if (BaseServices.reauthInFlight) return state;
                return showUnique(
                    state,
                    "http-unauthorized",
                    {
                        message: "Looks like your session has terminated. Probably because there was network connectivity issues which prevented us from keeping it alive like we usually do.\n\nDo you want to reload the app so you can reauthenticate?",
                        actions: [ okAction(() => {
                            console.info("User requested reauth");
                            this.__dispatcher.dispatch({
                                type: OverActions.SHOW,
                                message: "Reloading...",
                            });
                            BaseServices.navigateToAuthUrl();
                        }), {
                            label: "Cancel",
                            callback: () =>
                                console.warn("User declined reauth"),
                        } ],
                    },
                );

            case OverActions.HTTP_FORBIDDEN:
            case OverActions.HTTP_NOT_FOUND:
                return showUnique(
                    state,
                    "http-not-found",
                    {
                        message: "You have requested a non-existent resource.",
                        actions: [ okAction() ],
                    },
                );

            // new-style implicit actions
            case NavigationActions.SELECT_RECORD:
                this.__dispatcher.waitFor([
                    NavigatorStore.getDispatchToken(),
                ]);
                if (!NavigatorStore.hasChanged()) return state;
                return show(state, {
                    key: ENTITY_FORM_OVER_KEY,
                });

            case FormActions.START_LOAD:
            case ActiveFormActions.START_RENDER:
                return show(state, {
                    key: ENTITY_FORM_OVER_KEY,
                });

            case FormActions.LOAD_ERROR:
            case ActiveFormActions.RENDERED:
            case ActiveFormActions.NO_FORM_TO_RENDER:
                return hide(state, ENTITY_FORM_OVER_KEY, true);

            case RecordActions.SET_FIELD:
                if (isUnconfirmedVersionChange(TableStore, action)) {
                    return confirmDispatch(
                        state,
                        action,
                        () => <React.Fragment>
                            <p>The value you entered will result in a data version form change. Would you like to proceed?</p>
                            <p>Select Yes to change the data version.</p>
                            <p>Select No to retain the data version.</p>
                        </React.Fragment>,
                    );
                }
                return state;

            case UserActions.UPDATE_LICENSE_PREFERENCE:
            case UserActions.UPDATE_ORG_USER_PREFERENCE: {
                return show(state, "Saving...");
            }

            case UserActions.LICENSE_PREFERENCE_UPDATED:
            case UserActions.LICENSE_PREFERENCE_UPDATE_ERROR:
            case UserActions.ORG_USER_PREFERENCE_UPDATED:
            case UserActions.ORG_USER_PREFERENCE_UPDATE_ERROR: {
                return hide(state);
            }

            case SuperRecordActions.START_CREATE:
            case SuperRecordActions.START_UPDATE: {
                if (!canSave()) {
                    return showAlert(state,
                        "Sorry, we are unable to save while your upload(s) are in progress.");
                }
                return show(state, "Saving...");
            }

            case SuperRecordActions.CREATED:
            case SuperRecordActions.UPDATED: {
                if(MergeStore.isReady()) return hide(state);
                return hide(state, MERGE_WORKFLOW_KEY); // Finally hide the merging overlay
            }

            case SuperRecordActions.CREATE_ERROR:
            case SuperRecordActions.UPDATE_ERROR: {
                state = hide(state);
                if (!MergeStore.isReady()) {
                    // error during global merge
                    state = hide(state, MERGE_WORKFLOW_KEY);
                }
                if (isShowing(state, CONNECTION_INTERRUPTED_KEY)) return state;
                const xhr = action.error;
                let message = "An error has occurred.";
                let title = undefined;
                if ( xhr.status === 400 ) {
                    const response = JSON.parse(xhr.responseText);
                    if (response.status === "ENTITY_KEY_VIOLATION") {
                        title = message;
                        message = response.message;
                    }
                }
                if ( xhr.status === 500 ) {
                    const response = JSON.parse(xhr.responseText);
                    if ( response.message.indexOf("by another transaction.") !== -1 ) {
                        return showOptLockAlert(
                            state,
                            "another user or process",
                            action.initiatingContext,
                            action.initiatingContext,
                            action.key, action.id,
                        );
                    }
                }
                console.error(message, title, xhr);
                return showAlert(
                    state,
                    message,
                    title,
                );
            }

            case GxepActions.START_LOAD: {
                return show(state, "Loading...");
            }

            case GxepActions.START_SAVE: {
                return show(state, "Saving...");
            }

            case GxepActions.LOADED:
            case GxepActions.LOAD_ERROR:
            case GxepActions.SAVED:
            case GxepActions.SAVE_ERROR: {
                return hide(state);
            }

            case SuperRecordActions.DELETE_RECORD: {
                if (isConfirmed(action)) {
                    return show(state, {
                        message: "Deleting...",
                        key: SUPER_RECORD_DELETE_KEY,
                    });
                }
                const stereoInfo = StereotypesStore.getStereotypeByKey(action.key);
                const sp = TableStore.getStereotypeProxy(action.key, action.id);
                const fluxContext = action[ACTION_CONTEXT_KEY];
                const srInfo = SuperRecordStore.getRecord(
                    action.key,
                    action.id,
                    fluxContext,
                );
                let labels;
                let childInfo;
                if (srInfo.topLevelIds != null) {
                    labels = [];
                    for (const mck of Object.keys(srInfo.topLevelIds)) {
                        for (const id of srInfo.topLevelIds[mck]) {
                            labels.push(TableStore.getEntityProxy(mck, id)
                                .getLabel());
                        }
                    }
                    if (labels.length > 0) {
                        childInfo = StereotypesStore.getStereotypeByKey(
                            stereoInfo.magicChildKey);
                    }
                }
                return confirmDispatch(
                    state,
                    action,
                    () => <React.Fragment>
                        <p>
                            Are you sure you want to delete this {stereoInfo.name} <strong>{sp.getLabel()}</strong>
                        </p>
                        {labels && labels.length > 0 && (
                            <React.Fragment>
                                {" "}
                                with
                                {" "}
                                {labels.length === 1
                                    ? childInfo.name
                                    : childInfo.pluralName}:
                                <ul>
                                    {labels.map((l, i) => <li key={i}>{l}</li>)}
                                </ul>
                            </React.Fragment>
                        )}
                        <p>
                            THIS ACTION CANNOT BE UNDONE.
                        </p>
                    </React.Fragment>,
                    `Yes, Delete ${stereoInfo.name}`,
                );
            }

            case SuperRecordActions.DELETED: {
                return hide(state, SUPER_RECORD_DELETE_KEY);
            }

            case SuperRecordActions.DELETE_ERROR: {
                state = hide(state, SUPER_RECORD_DELETE_KEY);
                if (isShowing(state, CONNECTION_INTERRUPTED_KEY)) return state;
                const stereoInfo = StereotypesStore.getStereotypeByKey(action.key);
                const xhr = action.error;
                let message = `We are unable to delete this ${stereoInfo.name} at this time.`;
                if (xhr.responseText) {
                    //xhr.responseText = '{"message": "This Patient has inaccessable Episodes (CathPCI)","impls": [{ "name": "CathPCI", "key": "cathpci" }],"grant": 8}'
                    const resp = JSON.parse(xhr.responseText);
                    if (resp.impls != null) {
                        message = `The ${stereoInfo.name} has records for registries to which you do not have access (`;
                        const regs = [];
                        for (const reg of resp.impls) {
                            regs.push(reg.name);
                        }
                        message += regs.join(",");
                        message += `).\n\nPlease consult your administrator to delete this ${stereoInfo.name}.`;
                    }
                }
                return showAlert(state, message);
            }

            case RecordActions.DELETE_RECORD: {
                if (isConfirmed(action)) return state;
                const ep = TableStore.getEntityProxy(
                    action.recordKey,
                    action.recordId,
                );
                return confirmDelete(state, action, ep);
            }

            case RecordActions.DELETE_CHILD: {
                if (isConfirmed(action)) return state;
                const ep = TableStore.getEntityProxy(
                    action.childKey,
                    action.childId,
                );
                return confirmDelete(state, action, ep);
            }

            case ActiveFormActions.BLUR_ELEMENT: {
                if (action.fieldKey == null) return state;
                this.__dispatcher.waitFor([
                    ActiveFormStore.getDispatchToken(),
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                const fluxContext = action[ACTION_CONTEXT_KEY];
                const val = lastBlurredValidation(
                    fluxContext,
                    TableStore,
                    ActiveFormStore,
                );
                if (val == null || isOk(val.code)) return state;
                const implKey = new EntityName(action.entityKey).implKey;
                const confirm = severity =>
                    userStore.getLicensePreference(
                        `${severity}-data-confirmation-on-blur`,
                        implKey);
                const focusField = key =>
                    Dispatcher.contextDispatch(fluxContext, {
                        type: NavigationActions.FOCUS_FIELD,
                        entityKey: action.entityKey,
                        recordId: action.recordId,
                        fieldKey: key,
                    });
                const focusNextField = key => {
                    const next = FormStore.getNextControl(
                        action.entityKey,
                        key,
                        true,
                    );
                    if (next == null) return;
                    focusField(next.key);
                };

                if (isIllegal(val.code) && confirm("illegal")) {
                    const illegalLabel = userStore.getIllegalLabel(implKey);
                    return showAlert(
                        state,
                        `The Field Contains ${illegalLabel} Data.\n ${val.message}`,
                        () => focusField(action.fieldKey),
                    );
                } else if (val.isUnusual && confirm("unusual")) {
                    return showAlert(
                        state,
                        `The Field Contains Unusual Data.\n ${val.message}`,
                        () => focusNextField(action.fieldKey),
                    );
                } else if (isWarning(val.code) && confirm("warning")) {
                    return showAlert(
                        state,
                        `Warning: ${val.message}`,
                        () => focusNextField(action.fieldKey),
                    );
                }
                return state;
            }

            case AdtActions.SEARCH:
            case HemoActions.SEARCH: {
                if (!action.interactive) return state;
                return show(state, {
                    key: MAPPING_PLUGIN_SEARCH_KEY,
                    message: "Searching...",
                });
            }

            case AdtActions.SEARCH_RESULT:
            case AdtActions.SEARCH_ERROR:
            case HemoActions.SEARCH_RESULT:
            case HemoActions.SEARCH_ERROR: {
                if (!action.interactive) return state;
                return hide(state, MAPPING_PLUGIN_SEARCH_KEY);
            }

            case SearchFormActions.INITIATE_SEARCH:
            case SearchFormActions.INITIATE_SEARCH_AND_SAVE_DEFAULT:
            case SearchFormActions.RESTORE_DEFAULT_AND_INITIATE_SEARCH:
            case SearchResultActions.START_SEARCH:
                if(action.showLoadingModal === false) {
                    return state;
                }
                return show(state, {
                    key: "search:" + action.key,
                    message: "Searching...",
                });

            case SearchResultActions.SEARCH_RESULT:
            case SearchResultActions.SEARCH_ERROR: {
                return hide(state, "search:" + action.key);
            }

            case MergeActions.DO_MERGE: {
                if (isConfirmed(action)) {
                    return show(state, {
                        key: MERGE_WORKFLOW_KEY,
                        message: "Merging...",
                    });
                }
                return confirmDispatch(
                    state,
                    action,
                    () => {
                        const {source, target} = MergeStore.getProxyPair();
                        const sourceChildren = getAutoMergeGlobalSummary(source);
                        const typeLabel = target.stereoInfo.name.toLowerCase();
                        const sourceLabel = <strong>{source.getLabel()} {source.getDetailString() && <small>{source.getDetailString()}</small>}</strong>;
                        // const targetLabel = <strong>{target.getLabel()} {target.getDetailString() && <small>{target.getDetailString()}</small>}</strong>;
                        return (
                            <React.Fragment>
                                <p>
                                    Are you sure you want to merge and delete this {typeLabel} {sourceLabel}:
                                </p>
                                <ul>
                                    {sourceChildren.map(g => (
                                        g.items.map((l, i) => <li key={i}>{l}</li>)
                                    ))}
                                </ul>
                                <p>
                                    THIS ACTION CANNOT BE UNDONE.
                                </p>
                            </React.Fragment>
                        );
                    },
                    "Yes, Merge and Delete"
                );
            }

            case MapDialogActions.SELECT_SOURCE:
            case MapDialogActions.IMPORT:
            case MapButtonActions.AUTOMAP_FROM:
            case MapButtonActions.AUTOMAP_TO_NEW_MAGIC_CHILD: {
                return show(state, {
                    key: MAPPING_WORKFLOW_KEY,
                    message: "Mapping...",
                });
            }

            case MappingActions.MODEL_LOAD_ERROR: {
                state = hide(state, MAPPING_WORKFLOW_KEY);
                return showAlert(state, "A non-recoverable error happened.");
            }

            case MappingActions.MODEL_LOADED: {
                this.__dispatcher.waitFor([
                    MappingStore.getDispatchToken(),
                ]);
                if (MappingStore.isInteractive()) {
                    return hide(state, MAPPING_WORKFLOW_KEY);
                } else {
                    return state;
                }
            }

            case MappingActions.IT_IS_DONE: {
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                    MappingStore.getDispatchToken(),
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                if(MergeStore.isReady()) {
                    // record merge is done here.
                    state = hide(state, MERGE_WORKFLOW_KEY);
                }
                return hide(state, MAPPING_WORKFLOW_KEY);
            }

            case OverActions.EXTERNAL_NAV: {
                this.__dispatcher.waitFor([
                    userEditedRecordStore.getDispatchToken(),
                    lockStore.getDispatchToken(),
                ]);
                if (isConfirmed(action)) {
                    const ret = show(state, {
                        message: action.message,
                    });
                    action.callback();
                    return ret;
                }
                if(hasUnsavedChanges()) {
                    // there are unsaved changes
                    return confirmDispatch(
                        state,
                        action,
                        "<p>Changes you made may not be saved.</p>\n" +
                        "<p>Select Cancel to review your changes.</p>\n" +
                        "<p>Select Ok to continue without saving.</p>",
                        "Ok",
                        "Cancel"
                    );
                }
                else {
                    // no edits continue with navigation.
                    action.callback();
                }
                return state;
            }

            case LockActions.LOCKSET_CREATED:
            case LockActions.LOCKSET_CONFIRMED: {
                this.__dispatcher.waitFor([
                    lockStore.getDispatchToken()
                ]);
                for (const ctxName of lockStore.getContextNames()) {
                    const ctx = lockStore.getState(ctxName);
                    const ol = ctx.optlock;
                    if (ol && ol.violated && !ol.dismissed) {
                        return showOptLockAlert(
                            state,
                            ol.violators,
                            ctxName,
                            ol.id,
                            ctx.key, ctx.id,
                            ctx.type,
                        );
                    }
                }
                return state;
            }

            case ExportParamActions.CREATE: {
                if (action.name) {
                    return show(state, {
                        key: EXPORT_PARAM_SET_KEY,
                        message: "Saving parameter selection..."
                    });
                }
                return showPrompt(
                    state,
                    "New Saved Parameter Selection",
                    "Name",
                    "",
                    name => {
                        if (!name) return;
                        name = name.trim();
                        if (!name) return;
                        this.__dispatcher.dispatch({
                            ...action,
                            name,
                        });
                    },
                );
            }

            case ExportParamActions.UPDATE: {
                return show(state, {
                    key: EXPORT_PARAM_SET_KEY,
                    message: "Saving parameter selection..."
                });
            }

            case ExportParamActions.RENAME: {
                if (action.name) {
                    return show(state, {
                        key: EXPORT_PARAM_SET_KEY,
                        message: "Renaming parameter selection..."
                    });
                }
                return showPrompt(
                    state,
                    "Rename Parameter Selection",
                    "Name",
                    action.oldName,
                    name => {
                        if (!name) return;
                        name = name.trim();
                        if (!name) return;
                        if (name === action.oldName) return;
                        this.__dispatcher.dispatch({
                            ...action,
                            name,
                        });
                    },
                );
            }

            case ExportParamActions.DELETE: {
                if (isConfirmed(action)) {
                    return show(state, {
                        key: EXPORT_PARAM_SET_KEY,
                        message: "Deleting parameter selection..."
                    });
                }
                return confirmDispatch(
                    state,
                    action,
                    "Are you sure you want to delete this parameter selection?"
                );
            }

            case ExportParamActions.CREATED:
            case ExportParamActions.RENAMED:
            case ExportParamActions.UPDATED:
            case ExportParamActions.DELETED: {
                return hide(state, EXPORT_PARAM_SET_KEY);
            }

            case ExportParamActions.CREATE_ERROR:
            case ExportParamActions.UPDATE_ERROR: {
                state = hide(state, EXPORT_PARAM_SET_KEY);
                return showAlert(state, "Failed to save parameter selection");
            }

            case ExportParamActions.RENAME_ERROR: {
                state = hide(state, EXPORT_PARAM_SET_KEY);
                return showAlert(state, "Failed to rename parameter selection");
            }

            case ExportParamActions.DELETE_ERROR: {
                state = hide(state, EXPORT_PARAM_SET_KEY);
                return showAlert(state, "Failed to delete parameter selection");
            }

            default:
                return state;
        }
    }

    isModalShowing() {
        return isModalShowing(this.getState());
    }

    isEmpty() {
        return isEmpty(this.getState());
    }

    getActiveItem() {
        return getActiveItem(this.getState());
    }

    isModalInteractive() {
        return isModalInteractive(this.getState());
    }
}

export const optionTypes = PropTypes.shape({
    __at: PropTypes.number.isRequired,
    message: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func, // a render callback
    ]).isRequired,
    actions: PropTypes.oneOfType([
        PropTypes.PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            keyCode: PropTypes.number, // button action trigger key
            callback: PropTypes.func, // not required (e.g., alert w/ no action on dismiss)
        })),
        PropTypes.func, // a render callback
    ]),
    title: PropTypes.string,
    autoHide: PropTypes.bool,
    messageIsUi: PropTypes.bool,
    interactive: PropTypes.bool,
    key: PropTypes.string.isRequired,
    spinner: PropTypes.bool,
});

OverStore.stateTypes = PropTypes.arrayOf(optionTypes);

export default typeCheckingStore(new OverStore());



// WEBPACK FOOTER //
// ./app/data/OverStore.js